import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Gavel, Search, Clock, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EventCard from "@/components/EventCard";
import { LEAGUE_COLORS } from "@/components/badges";
import { Button } from "@/components/ui/button";

const LEAGUES = ["ALL", "NFL", "NBA", "NHL", "MLB"];

export default function Rulings() {
  const { user } = useAuth();
  const isPro = !!(user && (["all_star", "founder"].includes(user.tier) || user.role === "admin"));
  const [rulings, setRulings] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [q, setQ] = useState("");
  const [delayed, setDelayed] = useState(false);
  const [delaySecs, setDelaySecs] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: 100 };
    if (league !== "ALL") params.league = league;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/rulings", { params });
    setRulings(data.rulings);
    setDelayed(data.delayed);
    setDelaySecs(data.delay_seconds || 0);
    setLoading(false);
  }, [league, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="max-w-[1000px] mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Gavel size={22} className="text-[#00E5A3]" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Official Rulings</h1>
          {delayed && (
            <span data-testid="rulings-delayed" className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB020]/15 border border-[#FFB020]/40 px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-[#FFB020]">
              <Clock size={11} /> Delayed ~{delaySecs >= 60 ? `${Math.round(delaySecs / 60)}m` : `${delaySecs}s`}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-400 mt-1">A permanent archive of the big, confirmed calls — ruled OUT, late scratches, suspensions, trades &amp; returns. Nothing scrolls away.</p>
      </div>

      {!isPro && (
        <div data-testid="rulings-upgrade" className="rounded-xl border border-[#00E5A3]/30 bg-[#00E5A3]/8 p-4 flex items-center justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-3">
            <Lock size={18} className="text-[#00E5A3]" />
            <p className="text-sm text-slate-200">Rulings shown on your plan's delay. <span className="text-slate-400">Go All-Star to see every ruling the moment it's official.</span></p>
          </div>
          <Button asChild className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-9 rounded-lg">
            <Link to="/pro" data-testid="rulings-upgrade-btn">Go All-Star</Link>
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {LEAGUES.map((l) => (
          <button key={l} data-testid={`rulings-league-${l.toLowerCase()}`} onClick={() => setLeague(l)}
            className={`px-3 py-1.5 rounded-lg text-sm font-mono font-semibold transition-colors border ${league === l ? "text-[#04140F] border-transparent" : "text-slate-300 border-[#232D42] hover:bg-[#151B2B]"}`}
            style={league === l ? { background: l === "ALL" ? "#00E5A3" : LEAGUE_COLORS[l] } : {}}>
            {l}
          </button>
        ))}
        <div className="flex items-center gap-2 flex-1 min-w-[180px] bg-[#151B2B] border border-[#232D42] rounded-lg px-3">
          <Search size={15} className="text-slate-500" />
          <input data-testid="rulings-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search player, team or headline…"
            className="flex-1 bg-transparent py-1.5 text-sm text-slate-200 outline-none placeholder:text-slate-600" />
        </div>
      </div>

      <div className="space-y-3" data-testid="rulings-list">
        {loading && <p className="text-slate-500 font-mono text-sm py-12 text-center">Loading rulings…</p>}
        {!loading && rulings.length === 0 && (
          <div className="text-center py-16 text-slate-500 font-mono text-sm">No official rulings match these filters yet.</div>
        )}
        {!loading && rulings.map((r) => <EventCard key={r.id} event={r} />)}
      </div>
    </div>
  );
}
